import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import MeLayout from "../MeLayout";
import { getSubmissionProgress } from "./creatorModels";
import {
  CREATOR_CONTACT_FIELDS,
  creatorProfilePath,
  loadCreatorContactStatus,
} from "./creatorContact";
import {
  CREATOR_LICENSE_PATH,
  creatorLicensePath,
  loadCreatorLicenseStatus,
} from "./creatorLicense";
import {
  formatCreatorBreadcrumbLabel,
  getCandidateId,
} from "../../../utils/candidateAuth";

/** Canonical breadcrumb trails for Creator pages */
export const CREATOR_CRUMBS = {
  scripts: [
    { label: "Creator", to: "/me/creator" },
    { label: "Scripts" },
  ],
  intro: [
    { label: "Creator", to: "/me/creator" },
    { label: "Personal intro" },
  ],
  work: [
    { label: "Creator", to: "/me/creator" },
    { label: "My work" },
  ],
  payments: [
    { label: "Creator", to: "/me/creator" },
    { label: "Payments" },
  ],
  scriptDetail: (code) => [
    { label: "Creator", to: "/me/creator" },
    { label: "Scripts", to: "/me/creator" },
    { label: code || "Script" },
  ],
  upload: (code) => [
    { label: "Creator", to: "/me/creator" },
    { label: "My work", to: "/me/creator/work" },
    { label: code || "Upload clips" },
  ],
  uploadWithTitle: (code, title) => [
    { label: "Creator", to: "/me/creator" },
    { label: "My work", to: "/me/creator/work" },
    { label: title ? `${code} · ${title}` : code || "Upload clips" },
  ],
};

function withCreatorIdCrumbs(breadcrumbs) {
  const idLabel = formatCreatorBreadcrumbLabel(getCandidateId());
  return (breadcrumbs || []).map((item) => {
    if (!item || item.label !== "Creator") return item;
    return { ...item, label: idLabel };
  });
}

function CreatorContactBanner({ nextPath = "/me/creator" }) {
  const [missing, setMissing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadCreatorContactStatus()
      .then((status) => {
        if (!cancelled) setMissing(status.complete ? [] : status.missing);
      })
      .catch(() => {
        if (!cancelled) setMissing(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!missing?.length) return null;

  const labels = CREATOR_CONTACT_FIELDS.filter((field) =>
    missing.includes(field.key)
  ).map((field) => field.label);

  return (
    <div className="creator-contact-banner" role="status">
      <div>
        <p className="creator-contact-banner__title">Complete your contact details</p>
        <p className="creator-contact-banner__copy">
          Add your {labels.join(", ")} so we can pay you and send retake feedback.
        </p>
      </div>
      <Link className="training-btn-primary" to={creatorProfilePath(nextPath)}>
        Update profile
      </Link>
    </div>
  );
}

function CreatorLicenseBanner({ nextPath = "/me/creator", mustAccept = false }) {
  if (!mustAccept) return null;

  return (
    <div className="creator-contact-banner creator-license-banner" role="status">
      <div>
        <p className="creator-contact-banner__title">Accept the Creator Content License</p>
        <p className="creator-contact-banner__copy">
          You must accept QikTruck&apos;s copyright rights before you start or submit a script.
        </p>
      </div>
      <Link className="training-btn-primary" to={creatorLicensePath(nextPath)}>
        Read and accept
      </Link>
    </div>
  );
}

export function CreatorLayout({
  title = "Creator",
  breadcrumbs = CREATOR_CRUMBS.scripts,
  showHub = true,
  backTo,
  contactNextPath,
  children,
}) {
  const [licenseMustAccept, setLicenseMustAccept] = useState(false);
  const linkClass = ({ isActive }) =>
    `creator-hub-link${isActive ? " is-active" : ""}`;

  const resolvedCrumbs = useMemo(
    () => withCreatorIdCrumbs(breadcrumbs),
    [breadcrumbs]
  );

  const resolvedBackTo =
    backTo ||
    [...(resolvedCrumbs || [])].reverse().find((item) => item.to)?.to ||
    "/creator";

  const gateNext = contactNextPath || "/me/creator";
  const resolvedTitle =
    title === "Creator" ? formatCreatorBreadcrumbLabel(getCandidateId()) : title;

  useEffect(() => {
    let cancelled = false;
    loadCreatorLicenseStatus()
      .then((status) => {
        if (!cancelled) setLicenseMustAccept(Boolean(status.mustAccept));
      })
      .catch(() => {
        if (!cancelled) setLicenseMustAccept(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <MeLayout
      title={resolvedTitle}
      backTo={resolvedBackTo}
      breadcrumbs={resolvedCrumbs}
      realm="creator"
    >
      {showHub ? (
        <nav className="creator-hub-nav" aria-label="Creator sections">
          <NavLink to="/me/creator" end className={linkClass}>
            Scripts
          </NavLink>
          <NavLink to="/me/creator/intro" className={linkClass}>
            Intro
          </NavLink>
          <NavLink to="/me/creator/work" className={linkClass}>
            My work
          </NavLink>
          <NavLink to="/me/creator/payments" className={linkClass}>
            Payments
          </NavLink>
          <NavLink to={CREATOR_LICENSE_PATH} className={linkClass}>
            License
          </NavLink>
        </nav>
      ) : null}
      <div
        className={`creator-shell${
          licenseMustAccept ? " creator-shell--license-pending" : ""
        }`}
      >
        <CreatorContactBanner nextPath={gateNext} />
        <CreatorLicenseBanner
          nextPath={gateNext}
          mustAccept={licenseMustAccept}
        />
        {children}
      </div>
    </MeLayout>
  );
}

export function CreatorStatusChip({ status }) {
  const map = {
    available: { label: "Available", tone: "orange" },
    expired: { label: "No longer available", tone: "slate" },
    in_progress: { label: "In progress", tone: "orange" },
    submitted: { label: "In review", tone: "slate" },
    retake: { label: "Needs retake", tone: "red" },
    approved: { label: "Approved", tone: "green" },
    rejected: { label: "Not approved", tone: "red" },
    uploaded: { label: "Clip ready", tone: "green" },
    needs_retake: { label: "Needs retake", tone: "red" },
    accepted: { label: "Accepted", tone: "green" },
    payable: { label: "Payable", tone: "green" },
    included_in_payout: { label: "Payable", tone: "green" },
    paid: { label: "Paid", tone: "slate" },
    pending: { label: "Pending", tone: "orange" },
  };
  const meta = map[status] || { label: status, tone: "neutral" };
  return (
    <span className={`creator-chip creator-chip--${meta.tone}`}>{meta.label}</span>
  );
}

/** Review outcome banner for My Work / upload (retake, reject, approve, in review). */
export function CreatorDecisionBanner({ status, note }) {
  const trimmed = String(note || "").trim();
  if (status === "retake") {
    if (!trimmed) return null;
    return (
      <div className="creator-brief-callout creator-brief-callout--avoid">
        <strong>Retake needed</strong>
        <p>{trimmed}</p>
      </div>
    );
  }
  if (status === "rejected") {
    return (
      <div className="creator-brief-callout creator-brief-callout--avoid">
        <strong>Not approved</strong>
        <p>
          {trimmed ||
            "This script did not pass review. It is closed for you for now. Other scripts may still be open."}
        </p>
      </div>
    );
  }
  if (status === "approved") {
    return (
      <div className="creator-brief-callout creator-brief-callout--do">
        <strong>Approved</strong>
        <p>
          {trimmed ||
            "This script is payable. Check Payments for payout status and banking details."}
        </p>
      </div>
    );
  }
  if (status === "submitted") {
    return (
      <div className="creator-brief-callout">
        <strong>In review</strong>
        <p>We have your clips. Payment stays pending until this script is approved.</p>
      </div>
    );
  }
  return null;
}

export function CreatorProgress({ submission, className = "", prefix = "" }) {
  const progress = getSubmissionProgress(submission);
  const accessibleLabel = `${prefix ? `${prefix}: ` : ""}${progress.label} ready`;
  return (
    <div className={`creator-work-progress${className ? ` ${className}` : ""}`}>
      <span className="creator-work-card__meta">{progress.label}</span>
      <progress
        className="creator-progress"
        max={progress.needed || 1}
        value={progress.ready}
        aria-label={accessibleLabel}
      />
    </div>
  );
}

export function formatClipCue(shot) {
  return [shot.camera_angle, shot.posture, shot.gesture].filter(Boolean).join(" · ");
}
